import * as React from 'react'
import { duration } from 'moment'
import { generatePath } from 'react-router-dom'
import { BusinessTripModel } from '@/app/models/businessTripModel'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import TableTextRow from '@/modules/drivers/shared/tableItems/TableTextRow'
import TableEmpty from '@/modules/drivers/shared/tableItems/TableEmpty'
import TableCheckbox from '@/modules/drivers/shared/tableItems/TableCheckbox'
import Checkbox from '@/modules/drivers/shared/Checkbox'
import { useSortableData } from '@/modules/drivers/hooks/useSortTable'
import {
  getGroupedBusinessTripsColumns,
  getBusinessTripDetailColumns,
} from '@/modules/drivers/constants/tables'
import {
  FaIcon,
  faIdBadge,
  faChevronDown,
  faChevronUp,
  faTrash,
  faExclamationSolid,
} from '@/lib/fa'
import FailureTooltip from './FailureTooltip'
import {
  useBusinessTripFailuresForScope,
  useBusinessTripFailuresStore,
  type BusinessTripFailure,
} from '@/modules/drivers/stores/businessTripFailuresStore'
import { formatTripTimespan, formatDateTime } from '@/app/common/dateHelper'

import { useDrivers } from '@/modules/shared/context/drivers/DriversContext'
import { LoaderIcon } from '@/app/features/icons/Loader'
import BusinessTripContextMenu, {
  BusinessTripMenuTarget,
} from './tables/BusinessTripContextMenu'
import BusinessTripContextMenuDialogs from './tables/BusinessTripContextMenuDialogs'
import useBusinessTripContextMenuActions from '../hooks/useBusinessTripContextMenuActions'
import { RouteComponentProps, withRouter } from '@/app/navigation/withRouter'
import { driversRoutesMap } from '@/modules/drivers/routes-config'
import {
  getCurrencyTableIssueReason,
  isCurrencyTableIssueCode,
} from '@/modules/shared/utils/currencyTableIssue'

type Props = {
  trips: BusinessTripModel[]
  companyId?: string
  onRefresh?: () => void
  allDrivers?: { fullName: string; id: number; isActive: boolean }[]
  showAuditDates?: boolean
  onDeleteTrip?: (id: number) => void
} & RouteComponentProps

interface AggregatedTripData {
  driver: any
  driverId: string
  surname: string
  name: string
  timeLengthAway: any
  timeLengthHome: any
  amount: number
  parts: number
  accommodations: number
  invoices: number
  loans: number
  count: number
  isTripSettled: boolean
  destinationCountry: string
  trips: BusinessTripModel[]
}

const getTripId = (trip: BusinessTripModel): number =>
  typeof trip.id === 'string' ? parseInt(trip.id, 10) : (trip.id as number)

const GroupedBusinessTripList: React.FC<Props> = ({
  trips,
  companyId,
  history,
  match,
  onRefresh,
  allDrivers,
  showAuditDates = false,
  onDeleteTrip,
}) => {
  const { t, tSafe } = useLocalize()

  const {
    hasMore,
    loadMore,
    isLoadingMore,
    isLoadingBasicDrivers,
    search,
    actualGroup,
  } = useDrivers()

  const [collapsedDrivers, setCollapsedDrivers] = React.useState<Set<string>>(
    new Set(),
  )

  const params =
    (match?.params as { from?: string; to?: string; companyId?: string }) || {}
  const from = params.from || ''
  const to = params.to || ''
  const companyIdFromParams = params.companyId || companyId || ''

  // Per-driver failures from the last batch generation for this company + period.
  const generationFailures = useBusinessTripFailuresForScope({
    companyId: companyIdFromParams,
    from,
    to,
  })

  // Markers reflect only the last calculation while the user is on this page —
  // drop them when leaving the business-trips view.
  React.useEffect(
    () => () => useBusinessTripFailuresStore.getState().clearFailures(),
    [],
  )

  const resolveFailureReason = (failure: BusinessTripFailure): string => {
    const fallback = failure.message || t('businessTrip.generate-failed')
    const code = failure.code
    if (!code) return fallback
    if (code === 'NO_ACTIVE_CONTRACT_IN_PERIOD') {
      return t('newDelegation.trips-no-contract-title')
    }
    if (isCurrencyTableIssueCode(code)) {
      // Powod + dzien, ktorego kurs dotyczy - inaczej marker mowil tylko "brak kursu"
      // i nie bylo wiadomo, ktora date poprawic w ustawieniach delegacji.
      return getCurrencyTableIssueReason(
        { code, date: failure.message ?? '' },
        t,
      )
    }
    // Most generation failure codes have a Polish `errorCode.<CODE>` entry; fall
    // back to the raw server message (or a generic notice) for the few that don't.
    return tSafe(`errorCode.${code}`, fallback)
  }

  const aggregatedData = React.useMemo<AggregatedTripData[]>(() => {
    const tripsByDriver = trips.reduce(
      (acc, trip) => {
        const driverId = String(trip.driver?.id || trip.driver || '')
        if (!acc[driverId]) acc[driverId] = []
        acc[driverId].push(trip)
        return acc
      },
      {} as Record<string, BusinessTripModel[]>,
    )

    const aggregateTripsForDriver = (driverTrips: BusinessTripModel[]): Omit<AggregatedTripData, 'driver' | 'driverId' | 'surname' | 'name'> => {
      const result = {
        timeLengthAway: duration(0),
        timeLengthHome: duration(0),
        amount: 0,
        parts: 0,
        accommodations: 0,
        invoices: 0,
        loans: 0,
        count: 0,
        isTripSettled: true,
        destinationCountry: '',
        trips: [] as BusinessTripModel[],
      }
      for (const trip of driverTrips) {
        result.trips.push(trip)
        result.timeLengthAway = result.timeLengthAway.add(duration(trip.timeLengthAway))
        result.timeLengthHome = result.timeLengthHome.add(duration(trip.timeLengthHome))
        result.amount += trip.amount || 0
        result.parts += trip.parts || 0
        result.accommodations += trip.accommodations || 0
        result.invoices += trip.invoices || 0
        result.loans += trip.loans || 0
        result.count += 1
        if (!trip.isTripSettled) result.isTripSettled = false
        if (!result.destinationCountry && trip.destinationCountry) {
          result.destinationCountry = trip.destinationCountry
        }
      }
      return result
    }

    let result: AggregatedTripData[]

    if (allDrivers && allDrivers.length > 0) {
      result = allDrivers
        .map((driver) => {
          const driverId = String(driver.id)
          const driverTrips = tripsByDriver[driverId] || []
          const nameParts = driver.fullName.split(' ')
          const surname = nameParts[0] || ''
          const name = nameParts.slice(1).join(' ')

          if (driverTrips.length > 0) {
            const tripDriver = driverTrips[0].driver
            return {
              driver: tripDriver || { id: driver.id, surname, name },
              driverId,
              surname: tripDriver?.surname || surname,
              name: tripDriver?.name || name,
              ...aggregateTripsForDriver(driverTrips),
            }
          }

          return {
            driver: { id: driver.id, surname, name },
            driverId,
            surname,
            name,
            timeLengthAway: duration(0),
            timeLengthHome: duration(0),
            amount: 0,
            parts: 0,
            accommodations: 0,
            invoices: 0,
            loans: 0,
            count: 0,
            isTripSettled: true,
            destinationCountry: '',
            trips: [],
          }
        })
    } else {
      result = Object.entries(tripsByDriver).map(([driverId, driverTrips]) => {
        const firstTrip = driverTrips[0]
        return {
          driver: firstTrip.driver,
          driverId,
          surname: firstTrip.driver?.surname || '',
          name: firstTrip.driver?.name || '',
          ...aggregateTripsForDriver(driverTrips),
        }
      })
    }

    return result.sort((a, b) => {
      const nameA = `${a.surname} ${a.name}`
      const nameB = `${b.surname} ${b.name}`
      return nameA.localeCompare(nameB)
    })
  }, [trips, allDrivers])

  const { sortedData, sortConfig, handleHeaderClick } = useSortableData({
    data: aggregatedData,
    tableColumns: getGroupedBusinessTripsColumns(t),
  })
  const scrollContainerRef = React.useRef<HTMLDivElement>(null)
  const sentinelRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return undefined

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore()
        }
      },
      { root: scrollContainerRef.current, threshold: 0.1 },
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [loadMore])

  const [menuTarget, setMenuTarget] =
    React.useState<BusinessTripMenuTarget | null>(null)

  // Zaznaczanie jak w WPF TripsListView: naglowek -> wszystkie, kierowca -> jego
  // delegacje, wiersz -> jedna. Menu kontekstowe dziala na zaznaczone.
  const [selectedTripIds, setSelectedTripIds] = React.useState<number[]>([])
  const selectedTripIdSet = React.useMemo(
    () => new Set(selectedTripIds),
    [selectedTripIds],
  )
  const allTripIds = React.useMemo(
    () => trips.map(getTripId).filter((id) => Number.isFinite(id)),
    [trips],
  )

  React.useEffect(() => {
    const present = new Set(allTripIds)
    setSelectedTripIds((ids) => {
      const kept = ids.filter((id) => present.has(id))
      return kept.length === ids.length ? ids : kept
    })
  }, [allTripIds])

  const setTripsSelected = (ids: number[], selected: boolean) => {
    setSelectedTripIds((current) => {
      if (selected) {
        const next = new Set(current)
        ids.forEach((id) => next.add(id))
        return Array.from(next)
      }
      const removed = new Set(ids)
      return current.filter((id) => !removed.has(id))
    })
  }

  const {
    dialogState,
    handleAction,
    closeDialog,
    handleConfirmCountry,
    handleConfirmSettlementMonth,
    handleConfirmSettlementDate,
    handleConfirmExchangeRateDate,
    handleConfirmVehicle,
  } = useBusinessTripContextMenuActions({
    companyId: companyIdFromParams,
    selectedIds: selectedTripIds,
    allTrips: trips,
    onRefresh: onRefresh || (() => {}),
  })

  const formatNumber = (value: number): string => {
    const num = Number(value.toFixed(2))
    if (num % 1 === 0) {
      return num.toString() + ' PLN'
    }
    return parseFloat(num.toFixed(2)).toString() + ' PLN'
  }

  const formatNumberWithoutPLN = (value: number): string => {
    const num = Number(value.toFixed(2))
    if (num % 1 === 0) {
      return num.toString()
    }
    return parseFloat(num.toFixed(2)).toString()
  }

  const handleToggleExpand = (driverId: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation() 
      e.preventDefault() 
    }
    setCollapsedDrivers((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(driverId)) {
        newSet.delete(driverId)
      } else {
        newSet.add(driverId)
      }
      return newSet
    })
  }

 

  const handleTripClick = (trip: BusinessTripModel) => {
    if (!companyIdFromParams || !history) return

    const tripId = typeof trip.id === 'string' ? parseInt(trip.id, 10) : trip.id
    const driverId = String(trip.driver?.id || trip.driver || '')

    if (!driverId || !tripId) return

    const editPath = generatePath(driversRoutesMap.driverBusinessTripEdit, {
      companyId: companyIdFromParams,
      driverId,
      id: String(tripId),
    })
    history.push(editPath)
  }

  // Format total time (ŁĄCZNY CZAS)
  const formatTotalTime = (
    timeLengthHome: any,
    timeLengthAway: any,
  ): string => {
    const total = duration(timeLengthHome).add(
      duration(timeLengthAway),
    )
    return formatTripTimespan(total)
  }

  return (
    <section className="tw-relative tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
      {isLoadingBasicDrivers && sortedData.length === 0 ? (
        <div className="tw-grid tw-place-items-center">
          <LoaderIcon className="tw-w-16 tw-animate-spin" />
        </div>
      ) : (
      <div
        ref={scrollContainerRef}
        className="tw-h-full tw-overflow-y-auto"
      >
        {sortedData.length > 0 ? (
          <table className="tw-w-full tw-table-auto tw-border-collapse !tw-rounded-3xl tw-pt-2">
            <TableHeader
              opacity
              rounded={true}
              sticky
              toggleAll={(checked) =>
                setSelectedTripIds(checked ? allTripIds : [])
              }
              isSelectedAny={selectedTripIds.length > 0}
              disabledToggle={allTripIds.length === 0}
              columns={getGroupedBusinessTripsColumns(t)}
              sortConfig={sortConfig}
              onHeaderClick={handleHeaderClick}
            />
            <tbody>
              {sortedData.map((item) => {
                const driverId = item.driverId || String(item.driver?.id || '')
                const driverName = item.driver
                  ? `${item.driver.surname || item.surname} ${item.driver.name || item.name}`
                  : `${item.surname} ${item.name}`
                const isExpanded = !collapsedDrivers.has(driverId)

                const driverTripIds = item.trips
                  .map(getTripId)
                  .filter((id) => Number.isFinite(id))
                const isDriverSelected =
                  driverTripIds.length > 0 &&
                  driverTripIds.every((id) => selectedTripIdSet.has(id))

                // Menu na wierszu kierowcy dotyczy wszystkich jego delegacji. Gdy kierowca
                // jest juz caly zaznaczony, zostawiamy zaznaczenie (moze obejmowac innych);
                // inaczej zaznaczamy dokladnie jego delegacje, zeby bylo widac, co sie zmieni.
                const openDriverMenu = (event: React.MouseEvent) => {
                  if (item.trips.length === 0) return
                  event.preventDefault()
                  if (!isDriverSelected) {
                    setSelectedTripIds(driverTripIds)
                  }
                  setMenuTarget({
                    trip: item.trips[0],
                    anchorPoint: { x: event.clientX, y: event.clientY },
                  })
                }

                const driverRowClassName = `notches-border--right tw-w-fit tw-cursor-pointer tw-border-b hover:tw-bg-primary/10 ${
                  isDriverSelected ? 'tw-bg-primary/10' : 'tw-bg-white'
                }`

                const driverFailure = generationFailures[driverId]

                const driverRowCells = (
                  <>
                    <TableCheckbox
                      disabled={driverTripIds.length === 0}
                      background
                      checked={isDriverSelected}
                      onChange={(checked) =>
                        setTripsSelected(driverTripIds, checked)
                      }
                    />

                    <TableTextRow
                      icon={
                        driverFailure ? (
                          <FailureTooltip
                            text={resolveFailureReason(driverFailure)}
                          >
                            <FaIcon
                              icon={faExclamationSolid}
                              className="tw-h-4 tw-w-4 tw-text-red-400"
                            />
                          </FailureTooltip>
                        ) : (
                          <FaIcon
                            icon={faIdBadge}
                            className="tw-h-4 tw-w-4 tw-text-content-base"
                          />
                        )
                      }
                    >
                      {driverName}
                    </TableTextRow>
                    <TableTextRow>
                      {formatTotalTime(
                        item.timeLengthHome,
                        item.timeLengthAway,
                      )}
                    </TableTextRow>
                    <TableTextRow>
                      {formatNumberWithoutPLN(item.accommodations)}
                    </TableTextRow>
                    <TableTextRow>
                      {formatNumberWithoutPLN(item.parts)}
                    </TableTextRow>
                    <td className="tw-px-3 tw-py-2.5 tw-text-content-base">
                      {driverTripIds.length > 0 && (
                        <span
                          className="tw-cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                            setCollapsedDrivers((prev) => {
                              const newSet = new Set(prev)
                              if (newSet.has(driverId)) {
                                newSet.delete(driverId)
                              } else {
                                newSet.add(driverId)
                              }
                              return newSet
                            })
                          }}
                        >
                          <FaIcon
                            icon={isExpanded ? faChevronUp : faChevronDown}
                            className="tw-h-4 tw-w-4 tw-text-content-base tw-transition-transform"
                          />
                        </span>
                      )}
                    </td>
                  </>
                )

                return (
                  <React.Fragment key={driverId}>
                    <tr
                      className={driverRowClassName}
                      onContextMenu={openDriverMenu}
                    >
                      {driverRowCells}
                    </tr>

                    {isExpanded && item.trips && item.trips.length > 0 && (
                      <>
                        <tr className="tw-bg-gray-50/50">
                          <td
                            colSpan={getGroupedBusinessTripsColumns(t).length + 2}
                            className="tw-p-0"
                          >
                            <div className="">
                              <table className="tw-w-full tw-border-collapse">
                                <thead>
                                  <tr className="tw-border-b tw-border-gray-200">
                                    <th className="tw-w-4 tw-bg-gray-50/50 tw-px-3 tw-py-2" />
                                    <th className="tw-w-4 tw-bg-gray-50/50 tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-uppercase tw-text-gray-600">
                                    </th>
                                    {getBusinessTripDetailColumns(t, {
                                      includeAuditDates: showAuditDates,
                                    }).map((col, colIdx) => (
                                      <th
                                        key={col.key ?? `hdr-${colIdx}`}
                                        className="tw-bg-gray-50/50 tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-uppercase tw-text-gray-600"
                                      >
                                        {col.title}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {item.trips.map((trip, tripIndex) => {
                                    const tripId = getTripId(trip)
                                    const isTripSelected =
                                      selectedTripIdSet.has(tripId)

                                    return (
                                      <tr
                                        key={
                                          trip.id || trip.number || tripIndex
                                        }
                                        className={`tw-cursor-pointer tw-border-b tw-border-gray-100 hover:tw-bg-gray-50/30 ${
                                          isTripSelected
                                            ? 'tw-bg-primary/10 tw-font-medium'
                                            : 'tw-bg-white'
                                        }`}
                                        onClick={() =>
                                          Number.isFinite(tripId) &&
                                          setTripsSelected(
                                            [tripId],
                                            !isTripSelected,
                                          )
                                        }
                                        onDoubleClick={() =>
                                          handleTripClick(trip)
                                        }
                                        onContextMenu={(event) => {
                                          event.preventDefault()
                                          setMenuTarget({
                                            trip,
                                            anchorPoint: {
                                              x: event.clientX,
                                              y: event.clientY,
                                            },
                                          })
                                        }}
                                      >
                                        <td
                                          className="tw-w-4 tw-px-3 tw-py-2"
                                          onClick={(e) => e.stopPropagation()}
                                          onDoubleClick={(e) =>
                                            e.stopPropagation()
                                          }
                                        >
                                          {Number.isFinite(tripId) && (
                                            <Checkbox
                                              checked={isTripSelected}
                                              onChange={(checked) =>
                                                setTripsSelected(
                                                  [tripId],
                                                  checked,
                                                )
                                              }
                                            />
                                          )}
                                        </td>
                                        <td
                                          className="tw-w-4 tw-px-3 tw-py-2"
                                          onClick={(e) => e.stopPropagation()}
                                          onDoubleClick={(e) =>
                                            e.stopPropagation()
                                          }
                                        >
                                          {Number.isFinite(tripId) && (
                                            <button
                                              type="button"
                                              title="Usuń delegację"
                                              onClick={(e) => {
                                                e.stopPropagation()
                                                onDeleteTrip?.(tripId as number)
                                              }}
                                              className="tw-flex tw-items-center tw-justify-center tw-text-red-500 hover:tw-text-red-700"
                                            >
                                              <FaIcon
                                                icon={faTrash}
                                                className="tw-h-4 tw-w-4"
                                              />
                                            </button>
                                          )}
                                        </td>

                                        <td className="tw-px-3 tw-py-2 tw-text-sm tw-text-content-base">
                                          {trip.number || '-'}
                                        </td>

                                        <td className="tw-px-3 tw-py-2 tw-text-sm tw-text-content-base">
                                          {trip.registrationNumber || '-'}
                                        </td>

                                        <td className="tw-px-3 tw-py-2 tw-text-sm tw-text-content-base">
                                          {trip.startDate
                                            ? formatDateTime(trip.startDate)
                                            : '-'}
                                        </td>

                                        <td className="tw-px-3 tw-py-2 tw-text-sm tw-text-content-base">
                                          {trip.endDate
                                            ? formatDateTime(trip.endDate)
                                            : '-'}
                                        </td>

                                        <td className="tw-px-3 tw-py-2 tw-text-sm tw-text-content-base">
                                          {`${formatTripTimespan(
                                            trip.timeLengthHome,
                                          )} / ${formatTripTimespan(
                                            trip.timeLengthAway,
                                          )}`}
                                        </td>

                                        <td className="tw-px-3 tw-py-2 tw-text-sm tw-text-content-base">
                                          {formatNumberWithoutPLN(
                                            trip.accommodations || 0,
                                          )}
                                        </td>

                                        <td className="tw-px-3 tw-py-2 tw-text-sm tw-text-content-base">
                                          {formatNumberWithoutPLN(
                                            trip.invoices || 0,
                                          )}
                                        </td>

                                        <td className="tw-px-3 tw-py-2 tw-text-sm tw-text-content-base">
                                          {formatNumberWithoutPLN(
                                            trip.loans || 0,
                                          )}
                                        </td>

                                        <td className="tw-px-3 tw-py-2 tw-text-sm tw-text-content-base">
                                          {formatNumberWithoutPLN(
                                            trip.parts || 0,
                                          )}
                                        </td>

                                        <td className="tw-px-3 tw-py-2 tw-text-sm tw-text-content-base">
                                          {trip.destinationCountry || '-'}
                                        </td>

                                        {showAuditDates && (
                                          <>
                                            <td className="tw-px-3 tw-py-2 tw-text-sm tw-text-content-base">
                                              {trip.creationDate?.isValid()
                                                ? formatDateTime(trip.creationDate)
                                                : '-'}
                                            </td>
                                            <td className="tw-px-3 tw-py-2 tw-text-sm tw-text-content-base">
                                              {trip.modificationDate?.isValid()
                                                ? formatDateTime(trip.modificationDate)
                                                : '-'}
                                            </td>
                                          </>
                                        )}
                                      </tr>
                                    )
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      </>
                    )}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        ) : (
          <table className="tw-w-full tw-table-auto tw-border-collapse !tw-rounded-3xl tw-pt-2">
            <TableHeader
              opacity
              rounded={true}
              disabledCheckboxToggle
              toggleAll={() => {}}
              isSelectedAny={false}
              columns={getGroupedBusinessTripsColumns(t)}
              sortConfig={sortConfig}
              disabledToggle={true}
            />
            <tbody>
              <tr>
                <td colSpan={getGroupedBusinessTripsColumns(t).length + 2}>
                  <TableEmpty
                    title={t('businessTrip.no-data')}
                  />
                </td>
              </tr>
            </tbody>
          </table>
        )}
        {hasMore && (
          <div ref={sentinelRef} className="tw-flex tw-justify-center tw-py-4">
            {isLoadingMore && <LoaderIcon className="tw-w-8 tw-animate-spin" />}
          </div>
        )}
      </div>
      )}

      <BusinessTripContextMenu
          target={menuTarget}
          onClose={() => setMenuTarget(null)}
          onAction={handleAction}
        />
      <BusinessTripContextMenuDialogs
        dialogState={dialogState}
        companyId={companyIdFromParams}
        exchangeRateDate={(dialogState.trip as any)?.exchangeRateDate ?? ''}
        onClose={closeDialog}
        onConfirmCountry={handleConfirmCountry}
        onConfirmSettlementMonth={handleConfirmSettlementMonth}
        onConfirmSettlementDate={handleConfirmSettlementDate}
        onConfirmExchangeRateDate={handleConfirmExchangeRateDate}
        onConfirmVehicle={handleConfirmVehicle}
      />
    </section>
  )
}

export default withRouter(GroupedBusinessTripList)
